import styled from "styled-components";
import Tooltip from "@mui/material/Tooltip";
import { TextEntryFormControl } from "../TextEntryFormControl";
import { TextControlStyle } from "../../Utilities/FormDesignConstants";
import { StyledFormHelperText, StyledTextField } from "./CommonStyled";
import { IssueData } from "../../Utilities/ControlRenderContext";
import { IDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import { StringDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/StringDataDefinition";
import { StateManager } from "../../../rule-engine-core/Common/StateManager";
import { DataUtilities } from "../../../rule-engine-core/Common/DataUtilities";
import { UIColorPickerTextField } from "../../../CommonUI/BuildlingBlocks/UIColorPickerTextField";
import { UISpacedBoxContent } from "../../../CommonUI/BuildlingBlocks/UISpacedBoxContent";
import { UILabel } from "../../../CommonUI/BuildlingBlocks/UILabel";

//@RegisterUIControl('Data (Entry)', 'Text Field', ControlType.Control, <TextFieldIcon />)
export class ColorPickerFormControl extends TextEntryFormControl {
    readonly __type: string = "Color Picker";
    private showPicker: boolean = false;

    onClick = () => {
        this.showPicker = true;
        StateManager.propertyChanged(this, "showPicker");
    };

    onFocus = () => {
        this.showPicker = true;
        StateManager.propertyChanged(this, "showPicker");
    };

    render() {
        let style = this.getFormContext()?.form?.defaultTextStyle;

        if (this.overrideStyle && this.style) style = this.style;

        let size: "medium" | "small" = this.getFormContext()?.form?.defaultTextSize ?? "medium";

        if (this.overrideStyle && this.size) size = this.size;

        const issueData: IssueData | null = this.getFormContext()!.getControlContext(this)!.getRuntimeIssueData();
        const issueText: string = issueData?.text || "";
        const customWidth = this.fullWidth ? "100%" : this.width ? `${this.width}px` : "200px";

        switch (style) {
            case TextControlStyle.LABELED:
                return (
                    <StyledTextFieldWrap
                        onClick={this.onClick}
                        background={this.getValue()}
                        width={customWidth}
                    >
                        {this.label && <UILabel variant={"form_label"}>{this.label}</UILabel>}
                        <UIColorPickerTextField
                            size={size}
                            data-size={size}
                            onFocus={this.onFocus}
                            data-testid={this.valueName}
                            onChange={() => {}}
                            variant={"outlined"}
                            fullWidth={this.fullWidth}
                            value={this.getValue()}
                            error={issueText}
                        />
                        {issueText && issueText.length > 30 && (
                            <Tooltip
                                title={issueText}
                                arrow
                                placement="bottom"
                            >
                                <StyledFormHelperText
                                    error={Boolean(issueText)}
                                    data-testid={`${this.valueName || "colorpicker"}-hinttext`}
                                >
                                    {issueText.slice(0, 30) + "..."}
                                </StyledFormHelperText>
                            </Tooltip>
                        )}
                        {((issueText && issueText.length < 31) || this.hintText) && (
                            <StyledFormHelperText
                                error={Boolean(issueText)}
                                data-testid={`${this.valueName || "colorpicker"}-hinttext`}
                            >
                                {issueText ? issueText : this.hintText}
                            </StyledFormHelperText>
                        )}
                    </StyledTextFieldWrap>
                );
            case TextControlStyle.SHADED:
                return (
                    <StyledTextFieldWrap
                        width={customWidth}
                        onClick={this.onClick}
                        background={this.getValue()}
                    >
                        <UIColorPickerTextField
                            size={size}
                            data-size={size}
                            onFocus={this.onFocus}
                            data-testid={this.valueName}
                            onChange={() => {}}
                            variant={"filled"}
                            fullWidth={this.fullWidth}
                            value={this.getValue()}
                            error={issueText}
                        />
                        {(issueText || this.hintText) && (
                            <StyledFormHelperText
                                error={Boolean(issueText)}
                                data-testid={`${this.valueName || "colorpicker"}-hinttext`}
                            >
                                {issueText ? issueText : this.hintText}
                            </StyledFormHelperText>
                        )}
                    </StyledTextFieldWrap>
                );
            case TextControlStyle.UNDERLINED:
                return (
                    <StyledTextFieldWrap
                        width={customWidth}
                        onClick={this.onClick}
                        background={this.getValue()}
                    >
                        <UIColorPickerTextField
                            size={size}
                            data-size={size}
                            onFocus={this.onFocus}
                            data-testid={this.valueName}
                            onChange={() => {}}
                            variant={"standard"}
                            fullWidth={this.fullWidth}
                            value={this.getValue()}
                            error={issueText}
                        />
                        {(issueText || this.hintText) && (
                            <StyledFormHelperText
                                error={Boolean(issueText)}
                                data-testid={`${this.valueName || "colorpicker"}-hinttext`}
                            >
                                {issueText ? issueText : this.hintText}
                            </StyledFormHelperText>
                        )}
                    </StyledTextFieldWrap>
                );
            case TextControlStyle.OUTLINE:
                return (
                    <StyledTextFieldWrap
                        width={customWidth}
                        onClick={this.onClick}
                        background={this.getValue()}
                    >
                        <StyledTextField
                            size={size}
                            data-size={size}
                            data-testid={this.valueName}
                            onChange={() => {}}
                            variant={"outlined"}
                            customstyle={"outlined"}
                            fullWidth={this.fullWidth}
                            width={customWidth}
                            value={this.getValue()}
                            InputProps={{
                                onFocus: this.onFocus,
                                readOnly: true,
                                inputRef: this.ref,
                            }}
                            error={Boolean(issueText)}
                            label={this.valueName}
                        />
                        {(issueText || this.hintText) && (
                            <StyledFormHelperText
                                error={Boolean(issueText)}
                                data-testid={`${this.valueName || "colorpicker"}-hinttext`}
                            >
                                {issueText ? issueText : this.hintText}
                            </StyledFormHelperText>
                        )}
                    </StyledTextFieldWrap>
                );
        }
    }

    renderEditUI(): JSX.Element | null {
        return <XColorPickerEditUI editMe={this} />;
    }

    getAddedData(): IDataDefinition | IDataDefinition[] | undefined {
        if (!this.valueName) return;

        let def = new StringDataDefinition();
        def.name = this.valueName;
        return def;
    }

    getStorageData(): object {
        let retData = super.getStorageData();

        retData = DataUtilities.addStorageData(this, retData, "__type");

        return retData;
    }

    setStorageData(data: object): void {
        super.setStorageData(data);

        DataUtilities.setStorageData(this, data, "__type");
    }

    private handleChange = (color: string) => {
        this.showPicker = false;
        StateManager.propertyChanged(this, "showPicker");

        if (this.valueName) {
            this.setFormDataValue(this.valueName, color);
        }
    };

    private getValue = () => {
        if (this.valueName) {
            return this.getFormDataValue(this.valueName);
        }
    };
}

function XColorPickerEditUI(props: { editMe: ColorPickerFormControl }) {
    return (
        <UISpacedBoxContent>
            {props.editMe.renderBaseDataControlEditor()}
            {props.editMe.renderTextControlBasePropertyEditor()}
            {props.editMe.renderTextStyleSelectionEditor()}
        </UISpacedBoxContent>
    );
}

const StyledTextFieldWrap = styled.div<{ width?: string; background?: string }>`
    width: ${({ width }) => width} !important;
    height: 100% !important;
    box-sizing: border-box !important;
    position: relative !important;

    input {
        background: ${(props) => props.background || "transparent"} !important;
    }
`;
